import type { LanguageCode } from "../api/contracts/language-settings";

/**
 * The language map, with nothing imported around it.
 *
 * `utils/language.ts` pulls in i18next, date-fns locales and the country-name
 * tables, which is fine in the booking engine and far too much for the embedded
 * widgets: every host page downloads those, and the booking widget is held to a
 * 90 KB gzip ceiling. Keeping the codes here lets the widgets speak the same
 * four languages as the engine without the weight behind them.
 */

/**
 * The i18n resource bundle each language is served from. i18next keys its
 * bundles on the bare subtag while everything else in the product speaks
 * `LanguageCode`, so every boundary between the two goes through this pair.
 */
export const I18N_LANGUAGES = {
  "sv-SE": "sv",
  "en-GB": "en",
  "de-DE": "de",
  "fr-FR": "fr",
} as const satisfies Record<LanguageCode, string>;

export type I18nLanguage = (typeof I18N_LANGUAGES)[LanguageCode];

const BY_SUBTAG: Record<string, LanguageCode> = Object.fromEntries(
  Object.entries(I18N_LANGUAGES).map(([code, subtag]) => [
    subtag,
    code as LanguageCode,
  ]),
);

/**
 * The three-letter forms of the same four languages.
 *
 * A browser never sends these, but an embedding page's `<html lang>` is written
 * by whoever built that site, and ISO 639-2 does turn up there. Falling back to
 * Swedish for a page that plainly said English is worse than carrying six
 * aliases.
 */
const BY_ALIAS: Record<string, LanguageCode> = {
  swe: "sv-SE",
  eng: "en-GB",
  ger: "de-DE",
  deu: "de-DE",
  fre: "fr-FR",
  fra: "fr-FR",
};

/**
 * The primary subtag of a language tag.
 *
 * Splits on `_` as well as `-`: BCP 47 says hyphen, but CMS locale strings
 * (`en_US`) reach `<html lang>` often enough that treating one as unknown
 * would cost a correctly-declared page its language.
 */
const primarySubtag = (tag: string | null | undefined): string =>
  (tag ?? "").trim().toLowerCase().split(/[-_]/)[0];

/**
 * The language code for an i18n language or a browser tag.
 *
 * Matches on the primary subtag rather than the whole string, so the regional
 * variants a browser actually sends — `de-AT`, `fr-CH`, `en-US`, `sv-FI` —
 * land on the language we serve instead of falling through to English.
 */
export const languageCodeFromTag = (
  tag: string | null | undefined,
): LanguageCode => {
  const subtag = primarySubtag(tag);
  return BY_SUBTAG[subtag] ?? BY_ALIAS[subtag] ?? "en-GB";
};

/** The i18n bundle name for a language code. */
export const getI18nLanguage = (code: LanguageCode): I18nLanguage =>
  I18N_LANGUAGES[code];

/**
 * The bundle a tag asks for, or `undefined` when it asks for one we do not
 * serve.
 *
 * `languageCodeFromTag` answers English for anything it does not recognise, so
 * a caller that has to tell "asked for English" apart from "asked for something
 * we don't have" — the widgets, which fall back to Swedish and say so in the
 * console — needs the match confirmed rather than assumed.
 */
export const servedI18nLanguage = (
  tag: string | null | undefined,
): I18nLanguage | undefined => {
  const subtag = primarySubtag(tag);
  if (!subtag) return undefined;
  const known = BY_SUBTAG[subtag] ?? BY_ALIAS[subtag];
  return known ? I18N_LANGUAGES[known] : undefined;
};

/**
 * The best of a requested language and the ones a site actually serves.
 *
 * An embedded widget's language is decided by the host page, which can ask for
 * one the site has no translated content for. The ad endpoints answer with a
 * null name for a language that has no entry, and a card with no title is a
 * worse answer than a card in English — so a language the site does not offer
 * is swapped for English, or for the site's own first choice when it does not
 * offer English either. The same order `pickOfferedLanguage` uses in the
 * booking engine.
 *
 * An empty or missing list means the config did not say, and the request
 * stands: guessing would be worse than honouring what the host asked for.
 */
export const clampToOffered = (
  requested: LanguageCode,
  offered: LanguageCode[] | null | undefined,
): LanguageCode => {
  if (!offered || offered.length === 0) return requested;
  if (offered.includes(requested)) return requested;
  return offered.includes("en-GB") ? "en-GB" : offered[0];
};
